import { Box, NumberWrapper, OptionTypeEnum, setAlpha, STRIKE_CLASSNAME, VStack } from '@talos/kyoko';
import styled, { css } from 'styled-components';
import {
  ALL_OPTION_FIELDS,
  OPTION_STRIKE_PRICE,
  PRICE_ABOVE_STRIKE_CLASSNAME,
  PRICE_BELOW_STRIKE_CLASSNAME,
} from './types';
import { getOptionCellClassName } from './utils';

export const OptionWrapper = styled(Box)`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

export const OptionExpiryContainer = styled(VStack)`
  ${({ theme }) => css`
    /* Center PUTS and CALLS label */
    .ag-header-group-cell-label {
      margin-left: auto;
      margin-right: auto;
    }
    /* Hide the custom borders created by our blotter table styles. */
    .ag-header-group-cell::after {
      visibility: hidden;
    }

    .ag-header-row {
      /* Make the column to the right of the strike have a divider to the left */
      .ag-header-cell[col-id='Put.Position'] {
        ::before {
          background-color: ${theme.columnDividerDefault};
        }
        :hover::before {
          background-color: ${theme.columnDividerHover};
        }
      }

      /* Center and color the strike price header */
      .ag-header-cell.${STRIKE_CLASSNAME} {
        padding: 0;
        ::after,
        ::before {
          content: unset;
        }
        .ag-cell-label-container {
          padding: 0;
        }
        .ag-header-cell-label {
          border-top: 1px solid var(--ag-border-color);
          margin-top: 32px;
          justify-content: center;
          color: ${theme.colorTextImportant};
        }
      }
    }

    & {
      /* Make sure asterisk is shown for inline formatted numbers on greeks */
      ${NumberWrapper} {
        overflow: visible;
      }
    }

    --itm-background-color: ${setAlpha(0.2, theme.colors.primary.default)};
    --itm-background-color-hover: ${setAlpha(0.32, theme.colors.primary.lighten)};
    --itm-background-highlight-color: ${setAlpha(0.08, theme.colors.blue.default)};
    --itm-highlight-border-color: ${setAlpha(0.52, theme.colors.blue.default)};
    --otm-background-color-hover: ${setAlpha(0.08, theme.colors.gray['100'])};

    .ag-row {
      /* Strikes are gray and centered */
      .ag-cell.${STRIKE_CLASSNAME} {
        background-color: ${setAlpha(0.6, theme.colors.gray['030'])};
        justify-content: center;
        border-left: 1px solid var(--ag-row-border-color);
        border-right: 1px solid var(--ag-row-border-color);
      }

      /**
       * There is no use of having a selection color, since there is no selection in grid, but we cannot disable
       * selection since then we would lack copy row as json/csv
       */
      &.ag-row-selected {
        --ag-selected-row-background-color: transparent;
      }

      .ag-cell {
        /* Make sure hovered amounts are fully visible */
        overflow: visible;

        /* Make background color transition be delayed */
        transition-delay: 50ms;
        transition-duration: 100ms;
        &:hover {
          background-color: var(--otm-background-color-hover);
        }
      }

      &.${PRICE_BELOW_STRIKE_CLASSNAME}
        .${getOptionCellClassName(OptionTypeEnum.Call)},
        &.${PRICE_ABOVE_STRIKE_CLASSNAME}
        .${getOptionCellClassName(OptionTypeEnum.Put)} {
        background-color: var(--itm-background-color);
        &:hover {
          background-color: var(--itm-background-color-hover);
        }
      }

      /**
      * https://tobiasahlin.com/blog/previous-sibling-css-has/#selecting-all-preceding-siblings
      * Opposite cell highlighted when hovering cell
      * https://talostrading.atlassian.net/browse/UI-1601
      * NOTE that the selector for the hovered cell are both backward-looking
      * (:has(~ *:hover))
      * and forward-looking
      * (*:hover( ~ *))
      * That is because of ag-grid virtualization, which re-uses html elements and does
      * not enforce any ordering of them, so calls might appear after puts in the DOM even
      * though they are rendered to the left of them.
      **/

      /* If the row does not have style either below or above strike, color both as otm */
      & {
        ${ALL_OPTION_FIELDS.map(
          colId => css`
            & {
              .ag-cell[col-id='${OptionTypeEnum.Call}.${colId}']:has(
                  ~ .ag-cell[col-id='${OptionTypeEnum.Put}.${colId}']:hover
                ),
              .ag-cell[col-id='${OptionTypeEnum.Put}.${colId}']:hover
                ~ .ag-cell[col-id='${OptionTypeEnum.Call}.${colId}'] {
                background-color: var(--otm-background-color-hover);
              }
            }
            & {
              .ag-cell[col-id='${OptionTypeEnum.Put}.${colId}']:has(
                  ~ .ag-cell[col-id='${OptionTypeEnum.Call}.${colId}']:hover
                ),
              .ag-cell[col-id='${OptionTypeEnum.Call}.${colId}']:hover
                ~ .ag-cell[col-id='${OptionTypeEnum.Put}.${colId}'] {
                background-color: var(--otm-background-color-hover);
              }
            }
          `
        )}
      }

      &.${PRICE_BELOW_STRIKE_CLASSNAME} {
        /* If the row is price_below_strike, then calls are blue and puts gray */
        ${ALL_OPTION_FIELDS.map(
          colId => css`
            & {
              .ag-cell[col-id='${OptionTypeEnum.Call}.${colId}']:has(
                  ~ .ag-cell[col-id='${OptionTypeEnum.Put}.${colId}']:hover
                ),
              .ag-cell[col-id='${OptionTypeEnum.Put}.${colId}']:hover
                ~ .ag-cell[col-id='${OptionTypeEnum.Call}.${colId}'] {
                background-color: var(--itm-background-color-hover);
              }
            }
            & {
              .ag-cell[col-id='${OptionTypeEnum.Put}.${colId}']:has(
                  ~ .ag-cell[col-id='${OptionTypeEnum.Call}.${colId}']:hover
                ),
              .ag-cell[col-id='${OptionTypeEnum.Call}.${colId}']:hover
                ~ .ag-cell[col-id='${OptionTypeEnum.Put}.${colId}'] {
                background-color: var(--otm-background-color-hover);
              }
            }
          `
        )}
      }
      &.${PRICE_ABOVE_STRIKE_CLASSNAME} {
        /* If the row is price_above_strike, then calls are gray and puts blue */
        ${ALL_OPTION_FIELDS.map(
          colId => css`
            & {
              .ag-cell[col-id='${OptionTypeEnum.Call}.${colId}']:has(
                  ~ .ag-cell[col-id='${OptionTypeEnum.Put}.${colId}']:hover
                ),
              .ag-cell[col-id='${OptionTypeEnum.Put}.${colId}']:hover
                ~ .ag-cell[col-id='${OptionTypeEnum.Call}.${colId}'] {
                background-color: var(--otm-background-color-hover);
              }
            }

            & {
              .ag-cell[col-id='${OptionTypeEnum.Put}.${colId}']:has(
                  ~ .ag-cell[col-id='${OptionTypeEnum.Call}.${colId}']:hover
                ),
              .ag-cell[col-id='${OptionTypeEnum.Call}.${colId}']:hover
                ~ .ag-cell[col-id='${OptionTypeEnum.Put}.${colId}'] {
                background-color: var(--itm-background-color-hover);
              }
            }
          `
        )}
      }
    }
    .ag-row {
      /* Add pliancy to all cells... */
      .ag-cell[col-id] {
        cursor: pointer;
      }
      /* ...except StrikePrice. */
      .ag-cell[col-id='${OPTION_STRIKE_PRICE}'] {
        cursor: unset;
      }

      .ag-cell:has(div[data-highlight='true']) {
        &:after,
        &:before {
          content: '';
          bottom: 1px;
          position: absolute;
          left: 1px;
          top: 1px;
          right: 1px;
          pointer-events: none;
        }

        &:after {
          background: var(--itm-background-highlight-color);
          opacity: 0.92;
        }

        &:before {
          background:
            linear-gradient(to right, var(--itm-highlight-border-color) 1px, transparent 1px) 0 0,
            linear-gradient(to right, var(--itm-highlight-border-color) 1px, transparent 1px) 0 100%,
            linear-gradient(to left, var(--itm-highlight-border-color) 1px, transparent 1px) 100% 0,
            linear-gradient(to left, var(--itm-highlight-border-color) 1px, transparent 1px) 100% 100%,
            linear-gradient(to bottom, var(--itm-highlight-border-color) 1px, transparent 1px) 0 0,
            linear-gradient(to bottom, var(--itm-highlight-border-color) 1px, transparent 1px) 100% 0,
            linear-gradient(to top, var(--itm-highlight-border-color) 1px, transparent 1px) 0 100%,
            linear-gradient(to top, var(--itm-highlight-border-color) 1px, transparent 1px) 100% 100%;
          background-repeat: no-repeat;
          background-size: 5px 5px;
        }
      }
    }
  `}
`;
